import React, { useCallback } from "react";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faEye, faPause, faPlay } from "@fortawesome/free-solid-svg-icons";
import { MessageDescriptor, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";
import SweetAlert from "sweetalert2";
import toast from "react-hot-toast";
import { APP_COLORS } from "../../../witadmin/Common/constants";
import { DefaultTableRowActionsProps, Delete } from "../../../shared/actions/tableActions";
import { ExperimentEntity, ExperimentStatus } from "../domain/experiment.entity";
import { ExperimentRepository } from "../domain/interfaces/ExperimentRepository";
import { useExperimentMutations } from "../application/hooks/useExperimentMutations";
import { experimentsQueryKeys } from "../infrastructure/cache/ExperimentsCacheStrategy";
import { messages } from "../experiment.messages";

// The row-actions cell reads status off any experiment-derived row shape; it does not care about
// (nor re-shape) whatever extra flattened fields the list's own `rows` adapter adds on top of
// `ExperimentEntity` (US-008/US-009's concern).
export type ExperimentRow = ExperimentEntity & Record<string, unknown>;

export interface ExperimentRowActionsProps extends DefaultTableRowActionsProps<ExperimentRow> {
  propertyTicker: string;
  repository: ExperimentRepository;
  onViewResults(id: string): void;
  onDelete(id: string): Promise<void>;
}

interface StatusActionConfig {
  targetStatus: ExperimentStatus;
  icon: IconDefinition;
  variant: string;
  tooltip: MessageDescriptor;
  // Absent => no confirmation modal (Start only).
  confirm?: { title: MessageDescriptor; text: MessageDescriptor };
  success: MessageDescriptor;
  error: MessageDescriptor;
}

/**
 * One lifecycle-transition button, self-contained per instance (mirrors tableActions.tsx's own
 * `Delete` component, which is likewise instantiated once per row). Calls
 * `useExperimentMutations(propertyTicker, repository)` itself, rather than receiving `changeStatus`
 * as a prop from a single, page-level call in `index.tsx`, so that `isChangingStatus` is correctly
 * isolated per row/button instance instead of being shared page-wide.
 */
function StatusActionButton({
  row,
  config,
  propertyTicker,
  repository,
}: {
  row: ExperimentRow;
  config: StatusActionConfig;
  propertyTicker: string;
  repository: ExperimentRepository;
}) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { changeStatus, isChangingStatus } = useExperimentMutations(propertyTicker, repository);

  const execute = useCallback(async () => {
    if (config.confirm) {
      const result = await SweetAlert.fire({
        title: intl.formatMessage(config.confirm.title),
        text: intl.formatMessage(config.confirm.text),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: APP_COLORS.primary,
        cancelButtonColor: APP_COLORS.secondary,
        confirmButtonText: intl.formatMessage(messages.rowActions.confirmAccept),
        cancelButtonText: intl.formatMessage(messages.rowActions.confirmCancel),
      });
      if (!result.isConfirmed) return;
    }
    try {
      await changeStatus({ id: row.id, status: config.targetStatus });
      toast.success(intl.formatMessage(config.success));
    } catch (e) {
      console.error("Error changing experiment status", e);
      // Self-heal a stale row: changeStatus's own onError never invalidates, so without this the
      // row would keep offering the same now-invalid button after a rejected transition.
      await queryClient.invalidateQueries({ queryKey: experimentsQueryKeys.lists() });
      toast.error(intl.formatMessage(config.error));
    }
  }, [row.id, config, intl, changeStatus, queryClient]);

  return (
    <OverlayTrigger
      delay={{ show: 250, hide: 0 }}
      overlay={props => (
        <Tooltip id={`status-action-${config.targetStatus}-${row.id}`} {...props}>
          {intl.formatMessage(config.tooltip)}
        </Tooltip>
      )}>
      <Button
        disabled={isChangingStatus}
        size="sm"
        variant={config.variant}
        className="mr-1"
        onClick={execute}>
        <FontAwesomeIcon icon={config.icon} />
      </Button>
    </OverlayTrigger>
  );
}

// Pause and Resume both use the same warning/yellow treatment, matching the plan's explicit
// "pause/resume = yellow" wording. Start's color is implementer's discretion (the plan is silent
// on it) and is left as "success" — a positive, launching action, visually distinct from
// Pause/Resume's shared warning tone.
const startConfig: StatusActionConfig = {
  targetStatus: ExperimentStatus.EXPERIMENT_STATUS_RUNNING,
  icon: faPlay,
  variant: "success",
  tooltip: messages.rowActions.start.tooltip,
  success: messages.rowActions.start.success,
  error: messages.rowActions.start.error,
};

const pauseConfig: StatusActionConfig = {
  targetStatus: ExperimentStatus.EXPERIMENT_STATUS_PAUSED,
  icon: faPause,
  variant: "warning",
  tooltip: messages.rowActions.pause.tooltip,
  confirm: {
    title: messages.rowActions.pause.confirmTitle,
    text: messages.rowActions.pause.confirmText,
  },
  success: messages.rowActions.pause.success,
  error: messages.rowActions.pause.error,
};

const resumeConfig: StatusActionConfig = {
  targetStatus: ExperimentStatus.EXPERIMENT_STATUS_RUNNING,
  icon: faPlay,
  variant: "warning",
  tooltip: messages.rowActions.resume.tooltip,
  confirm: {
    title: messages.rowActions.resume.confirmTitle,
    text: messages.rowActions.resume.confirmText,
  },
  success: messages.rowActions.resume.success,
  error: messages.rowActions.resume.error,
};

export function ExperimentRowActions({
  row,
  rest,
  onReloadList,
  propertyTicker,
  repository,
  onViewResults,
  onDelete,
}: ExperimentRowActionsProps) {
  const intl = useIntl();
  const isDraftLike =
    row.status === ExperimentStatus.EXPERIMENT_STATUS_DRAFT ||
    row.status === ExperimentStatus.EXPERIMENT_STATUS_UNSPECIFIED;

  return (
    <Table.Cell {...rest} style={{ zIndex: 999 }}>
      {/* View results is offered for every status except Draft/Unspecified, which have no
          results yet (the experiment has never run). */}
      {!isDraftLike && (
        <OverlayTrigger
          delay={{ show: 250, hide: 0 }}
          overlay={props => (
            <Tooltip id={`view-results-${row.id}`} {...props}>
              {intl.formatMessage(messages.viewResults)}
            </Tooltip>
          )}>
          <Button size="sm" variant="light" className="mr-1" onClick={() => onViewResults(row.id)}>
            <FontAwesomeIcon icon={faEye} />
          </Button>
        </OverlayTrigger>
      )}

      {row.status === ExperimentStatus.EXPERIMENT_STATUS_RUNNING && (
        <StatusActionButton
          row={row}
          config={pauseConfig}
          propertyTicker={propertyTicker}
          repository={repository}
        />
      )}
      {row.status === ExperimentStatus.EXPERIMENT_STATUS_PAUSED && (
        <StatusActionButton
          row={row}
          config={resumeConfig}
          propertyTicker={propertyTicker}
          repository={repository}
        />
      )}
      {isDraftLike && (
        <StatusActionButton
          row={row}
          config={startConfig}
          propertyTicker={propertyTicker}
          repository={repository}
        />
      )}

      <Delete rest={rest} row={row} onDelete={onDelete} onReloadList={onReloadList} useOverlay />
    </Table.Cell>
  );
}
